import api from '@/shared/api/client'
import type {
  UserWithRoles,
  UserDetail,
  RoleSummary,
  RolePermissionsData,
  PermissionEntry,
  ClientRef,
  ClientListFilters,
  ListUsersFilters,
  UsersListMeta,
  PaginationInfo,
  CreateRoleRequest,
  CreateUserRequest,
  BulkRoleUpdateRequest,
  RoleRef,
  AuditLogEntry,
  AuditLogFilters,
  AuditLogStats,
  AuditLogFacets,
  SpecialtyRef,
  SpecialtyReportMapping,
  AuditActors,
  SpecialtyReportTypeMapping,
  ClientSpecialtyRef,
  PayerRef,
  AdminProvider,
  CreateProviderRequest,
  UpdateProviderRequest,
  ProviderListFilters,
  ProvisioningWizardData,
  UpdateClientRequest,
  FeatureCatalogRow,
  ClientFeatureRow,
  FeatureCatalogPatch,
  ClientMatrix,
} from '../types'

const BASE = '/system-admin'

export interface FacilityIntegrationConfig {
  id: string
  facility_id: string
  client_id: string
  emr_id: string
  input_mode: 'pdf' | 'excel'
  extractor_type: string | null
  extractor_module: string | null
  uses_provider_codes: boolean
  preprocess_pdf_text: boolean
  report_type_subbranch: unknown
  chart_data_path: string | null
  registration_data_path: string | null
  excel_source_path: string | null
  excel_transform_module: string | null
  uses_insurance_list_xlsx: boolean | null
  trigger_start_time: string | null
  trigger_end_time: string | null
  scheduler_cron: string | null
  is_event_driven: boolean
  discovery_lookback_days: number
  internal_sla_time_ist: string | null
  client_sla_time_ist: string | null
  sla_active: boolean
  rpa_login_url: string | null
  rpa_desktop_app_path: string | null
  rpa_processing_url: string | null
  rpa_credential_name: string | null
  patient_validation_fuzzy_config: unknown
  active: boolean
  notes: string | null
  created_at: string
  updated_at: string | null
  facility_name: string | null
  /** From t_org_facilities.is_active — separate from `active` (config's own
   *  flag). A row where facility_is_active is false is treated as inactive on
   *  the FE regardless of the config's own flag: no Edit / Activate actions. */
  facility_is_active: boolean
  emr_name: string | null
}

/** Write payload. `facility_id` is required on create only. */
export interface IntegrationConfigInput {
  facility_id?: string
  emr_id: string
  input_mode: 'pdf' | 'excel'
  extractor_type?: string | null
  extractor_module?: string | null
  uses_provider_codes?: boolean
  preprocess_pdf_text?: boolean
  report_type_subbranch?: unknown
  chart_data_path?: string | null
  registration_data_path?: string | null
  excel_source_path?: string | null
  excel_transform_module?: string | null
  uses_insurance_list_xlsx?: boolean | null
  trigger_start_time?: string | null
  trigger_end_time?: string | null
  scheduler_cron?: string | null
  is_event_driven?: boolean
  discovery_lookback_days?: number
  internal_sla_time_ist?: string | null
  client_sla_time_ist?: string | null
  sla_active?: boolean
  rpa_login_url?: string | null
  rpa_desktop_app_path?: string | null
  rpa_processing_url?: string | null
  rpa_credential_name?: string | null
  patient_validation_fuzzy_config?: unknown
  active?: boolean
  notes?: string | null
}

export const systemAdminApi = {
  // ── Users ──

  listUsers: (filters: ListUsersFilters = {}) =>
    api
      .get<{ data: UserWithRoles[]; pagination: PaginationInfo; meta?: UsersListMeta }>(`${BASE}/users`, {
        params: { limit: 25, offset: 0, ...filters },
      })
      .then((r) => r.data),

  getUserDetail: (userId: string) =>
    api.get<{ data: UserDetail }>(`${BASE}/users/${userId}`).then((r) => r.data.data),

  createUser: (body: CreateUserRequest) =>
    api
      .post<{ data: UserWithRoles & { welcome_email: { sent: boolean; error?: string } } }>(`${BASE}/users`, body)
      .then((r) => r.data.data),

  toggleUserStatus: (userId: string, active: boolean) =>
    api.patch<{ data: { status: string } }>(`${BASE}/users/${userId}/status`, { active }).then((r) => r.data.data),

  resendUserInvite: (userId: string) =>
    api
      .post<{ data: { email: string; welcome_email: { sent: boolean; error?: string } } }>(
        `${BASE}/users/${userId}/resend-invite`,
      )
      .then((r) => r.data.data),

  updateUserRoles: (userId: string, roleId: string) =>
    api
      .put<{ data: { role: RoleRef } }>(`${BASE}/users/${userId}/roles`, { role_id: roleId })
      .then((r) => r.data.data),

  bulkUpdateRoles: (body: BulkRoleUpdateRequest) =>
    api
      .post<{ data: { updated_count: number } }>(`${BASE}/users/bulk-roles`, body)
      .then((r) => r.data.data),

  // ── Roles ──

  listRoles: () =>
    api.get<{ data: RoleSummary[] }>(`${BASE}/roles`).then((r) => r.data.data),

  createRole: (body: CreateRoleRequest) =>
    api.post<{ data: { id: string; name: string } }>(`${BASE}/roles`, body).then((r) => r.data.data),

  updateRole: (roleId: string, body: { name?: string; description?: string }) =>
    api
      .put<{ data: { id: string; name: string; description: string | null } }>(
        `${BASE}/roles/${roleId}`,
        body,
      )
      .then((r) => r.data.data),

  deleteRole: (roleId: string) =>
    api.delete(`${BASE}/roles/${roleId}`),

  getRolePermissions: (roleId: string) =>
    api
      .get<{ data: RolePermissionsData }>(`${BASE}/roles/${roleId}/permissions`)
      .then((r) => r.data.data),

  replaceRolePermissions: (roleId: string, permissionIds: string[]) =>
    api
      .put<{ data: { permission_count: number } }>(`${BASE}/roles/${roleId}/permissions`, {
        permission_ids: permissionIds,
      })
      .then((r) => r.data.data),

  // ── Bucket grants — encounter-queue tab visibility per role ──

  getRoleBuckets: (roleId: string) =>
    api
      .get<{ data: {
        buckets: { slug: string; label: string; display_order: number }[];
        assigned: string[];
      } }>(`${BASE}/roles/${roleId}/buckets`)
      .then((r) => r.data.data),

  replaceRoleBuckets: (roleId: string, bucketSlugs: string[]) =>
    api
      .put<{ data: { bucket_count: number } }>(`${BASE}/roles/${roleId}/buckets`, {
        bucket_slugs: bucketSlugs,
      })
      .then((r) => r.data.data),

  // ── Reference Data ──

  listPermissions: () =>
    api.get<{ data: PermissionEntry[] }>(`${BASE}/permissions`).then((r) => r.data.data),

  listClients: (filters: ClientListFilters = {}) =>
    api.get<{ data: ClientRef[] }>(`${BASE}/clients`, { params: filters }).then((r) => r.data.data),

  /** Full envelope variant — used by the Clients page for real pagination
   *  (pagination.total) + per-status tab counts (meta.status_counts). */
  listClientsPaged: (filters: ClientListFilters = {}) =>
    api
      .get<{ data: ClientRef[]; pagination: PaginationInfo; meta?: { status_counts: Record<string, number> } }>(
        `${BASE}/clients`,
        { params: filters },
      )
      .then((r) => r.data),

  getClient: (clientId: string) =>
    api.get<{ data: ClientRef }>(`${BASE}/clients/${clientId}`).then((r) => r.data.data),

  createClient: (body: {
    name: string
    emr_id?: string | null
    timezone?: string
    contact_person?: string
    email?: string
    phone?: string
    official_website?: string
    avg_charts_per_day?: number
  }) =>
    api.post<{ data: { id: string; name: string; status: string } }>(`${BASE}/clients`, body).then((r) => r.data.data),

  updateClient: (clientId: string, body: UpdateClientRequest) =>
    api.patch<{ data: { id: string } }>(`${BASE}/clients/${clientId}`, body).then((r) => r.data.data),

  toggleClientStatus: (clientId: string, active: boolean) =>
    api.patch<{ data: { status: string } }>(`${BASE}/clients/${clientId}/status`, { active }).then((r) => r.data.data),

  // ── Facilities ──

  listClientFacilities: (clientId: string) =>
    api.get<{ data: ({
      id: string; name: string; npi: string | null; care_setting: string | null;
      street: string | null; zip: string | null; state_id: string | null; city_id: string | null;
      is_active: boolean; specialties: SpecialtyReportMapping[];
    } & AuditActors)[] }>(`${BASE}/clients/${clientId}/facilities`).then((r) => r.data.data),

  listClientSpecialties: (clientId: string) =>
    api.get<{ data: ClientSpecialtyRef[] }>(`${BASE}/clients/${clientId}/specialties`).then((r) => r.data.data),

  createFacility: (clientId: string, body: { name: string; npi?: string; care_setting?: string; street?: string; zip?: string; state_id?: string; city_id?: string }) =>
    api.post<{ data: { id: string; name: string; client_id: string } }>(`${BASE}/clients/${clientId}/facilities`, body).then((r) => r.data.data),

  updateFacility: (facilityId: string, body: {
    name?: string;
    npi?: string | null;
    care_setting?: string | null;
    /** Delivery model: 'mcaas' | 'saas' | null (20260907_011). */
    service_type?: string | null;
    street?: string | null;
    zip?: string | null;
    state_id?: string | null;
    city_id?: string | null;
  }) =>
    api.patch<{ data: { id: string; name: string; client_id: string } }>(`${BASE}/facilities/${facilityId}`, body).then((r) => r.data.data),

  toggleFacilityStatus: (facilityId: string, active: boolean) =>
    api.patch<{ data: { is_active: boolean } }>(`${BASE}/facilities/${facilityId}/status`, { active }).then((r) => r.data.data),

  // ── Integration config (t_int_facility_config) — client-scoped ──
  listEmrInfos: () =>
    api.get<{ data: { id: string; display_name: string }[] }>(`${BASE}/emr-infos`).then((r) => r.data.data),

  listIntegrationConfigs: (clientId: string) =>
    api.get<{ data: FacilityIntegrationConfig[] }>(`${BASE}/clients/${clientId}/integration-configs`).then((r) => r.data.data),

  createIntegrationConfig: (clientId: string, body: IntegrationConfigInput) =>
    api.post<{ data: { id: string; facility_id: string } }>(`${BASE}/clients/${clientId}/integration-configs`, body).then((r) => r.data.data),

  updateIntegrationConfig: (clientId: string, facilityId: string, body: IntegrationConfigInput) =>
    api.put<{ data: { id: string; facility_id: string } }>(`${BASE}/clients/${clientId}/integration-configs/${facilityId}`, body).then((r) => r.data.data),

  setIntegrationConfigStatus: (clientId: string, facilityId: string, active: boolean) =>
    api.patch<{ data: { facility_id: string; active: boolean } }>(`${BASE}/clients/${clientId}/integration-configs/${facilityId}/status`, { active }).then((r) => r.data.data),

  listStates: () =>
    api.get<{ data: { id: string; code: string; name: string }[] }>(`${BASE}/states`).then((r) => r.data.data),

  listCities: (stateId: string) =>
    api.get<{ data: { id: string; name: string; state_id: string }[] }>(`${BASE}/states/${stateId}/cities`).then((r) => r.data.data),

  // ── Specialties ──

  listSpecialties: () =>
    api.get<{ data: SpecialtyRef[] }>(`${BASE}/specialties`).then((r) => r.data.data),

  getFacilitySpecialties: (facilityId: string) =>
    api.get<{ data: SpecialtyReportMapping[] }>(`${BASE}/facilities/${facilityId}/specialties`).then((r) => r.data.data),

  updateFacilitySpecialties: (facilityId: string, specialtyMappings: SpecialtyReportTypeMapping[]) =>
    api.put<{ data: { specialty_count: number; row_count: number } }>(
      `${BASE}/facilities/${facilityId}/specialties`,
      { specialty_mappings: specialtyMappings },
    ).then((r) => r.data.data),

  // ── Payers ──

  listPayers: () =>
    api.get<{ data: PayerRef[] }>(`${BASE}/payers`).then((r) => r.data.data),

  // Active auto-assign owners for a client's facilities — used to warn before
  // reassigning a facility that already has an auto-assign user.
  listFacilityAutoAssignOwners: (clientId: string) =>
    api.get<{ data: { facility_id: string; user_id: string; first_name: string; last_name: string; email: string }[] }>(
      `${BASE}/clients/${clientId}/facility-auto-assign`,
    ).then((r) => r.data.data),

  getUserFacilities: (userId: string) =>
    api.get<{
      data: {
        id: string
        name: string
        client_id: string
        client_name: string
        auto_assign: boolean
        /** Is this user's work here billed to the client? Defaults true. */
        is_billable: boolean
      }[]
    }>(`${BASE}/users/${userId}/facilities`).then((r) => r.data.data),

  updateUserFacilities: (
    userId: string,
    facilityIds: string[],
    autoAssignFacilityIds?: string[],
    // When set, the edit is authoritative only for this client — the user's other
    // clients are left untouched (used by the per-client locked editor).
    scopeClientId?: string,
    // Facilities where this user's work is NOT billed to the client. Assignment
    // means billable, so this is the exception list.
    nonBillableFacilityIds?: string[],
  ) =>
    api.put<{ data: { facility_count: number } }>(`${BASE}/users/${userId}/facilities`, {
      facility_ids: facilityIds,
      ...(autoAssignFacilityIds !== undefined ? { auto_assign_facility_ids: autoAssignFacilityIds } : {}),
      ...(scopeClientId ? { scope_client_id: scopeClientId } : {}),
      ...(nonBillableFacilityIds !== undefined
        ? { non_billable_facility_ids: nonBillableFacilityIds }
        : {}),
    }).then((r) => r.data.data),

  // ── Impersonation ──

  startImpersonation: (userId: string) =>
    api.post<{ data: { access_token: string; expires_in: number; user: import('@/shared/stores/auth.store').AuthUser } }>(`${BASE}/impersonate`, { user_id: userId }).then((r) => r.data.data),

  endImpersonation: () =>
    api.post<{ data: { access_token: string; expires_in: number; user: import('@/shared/stores/auth.store').AuthUser } }>(`${BASE}/impersonate/end`).then((r) => r.data.data),

  // ── Audit Logs ──

  listAuditLogs: (filters: AuditLogFilters = {}) =>
    api
      .get<{ data: AuditLogEntry[]; pagination: PaginationInfo }>(`${BASE}/audit-logs`, {
        params: { limit: 25, offset: 0, ...filters },
      })
      .then((r) => r.data),

  getAuditLogDetail: (logId: string, createdAt: string) =>
    api
      .get<{ data: AuditLogEntry }>(`${BASE}/audit-logs/${logId}`, {
        params: { created_at: createdAt },
      })
      .then((r) => r.data.data),

  getAuditLogStats: (filters: { client_id?: string; date_from?: string; date_to?: string } = {}) =>
    api
      .get<{ data: AuditLogStats }>(`${BASE}/audit-logs/stats`, { params: filters })
      .then((r) => r.data.data),

  getAuditLogFacets: (filters: { client_id?: string } = {}) =>
    api
      .get<{ data: AuditLogFacets }>(`${BASE}/audit-logs/facets`, { params: filters })
      .then((r) => r.data.data),

  // ── Providers ──

  listProviders: (filters: ProviderListFilters = {}) =>
    api
      .get<{ data: AdminProvider[]; pagination: PaginationInfo; meta?: { active_count: number; inactive_count: number } }>(`${BASE}/providers`, {
        params: { limit: 25, offset: 0, sort_by: 'last_name', sort_order: 'asc', ...filters },
      })
      .then((r) => r.data),

  createProvider: (body: CreateProviderRequest) =>
    api
      .post<{ data: { id: string; first_name: string; last_name: string; client_id: string } }>(`${BASE}/providers`, body)
      .then((r) => r.data.data),

  updateProvider: (providerId: string, body: UpdateProviderRequest) =>
    api
      .put<{ data: { id: string } }>(`${BASE}/providers/${providerId}`, body)
      .then((r) => r.data.data),

  toggleProviderStatus: (providerId: string, active: boolean) =>
    api
      .patch<{ data: { is_active: boolean } }>(`${BASE}/providers/${providerId}/status`, { active })
      .then((r) => r.data.data),

  // ── Provisioning ──

  provisionClient: (data: ProvisioningWizardData) =>
    api
      .post<{ data: { client_id: string; name: string; status: string } }>(`${BASE}/provision`, data)
      .then((r) => r.data.data),

  // ── Client Feature Entitlements ──

  listFeatureCatalog: () =>
    api
      .get<{ data: FeatureCatalogRow[] }>(`${BASE}/feature-catalog`)
      .then((r) => r.data.data),

  listClientFeatures: (clientId: string) =>
    api
      .get<{ data: ClientFeatureRow[] }>(`${BASE}/clients/${clientId}/features`)
      .then((r) => r.data.data),

  /** All clients × client-scoped features, with each client's override. */
  getClientMatrix: () =>
    api.get<{ data: ClientMatrix }>(`${BASE}/entitlements/client-matrix`).then((r) => r.data.data),

  setClientFeature: (clientId: string, featureKey: string, value: unknown, facilityId?: string) =>
    api
      .put<{ data: { client_id: string; feature_key: string; facility_id: string | null; value: unknown } }>(
        `${BASE}/clients/${clientId}/features/${featureKey}`,
        facilityId ? { value, facility_id: facilityId } : { value },
      )
      .then((r) => r.data.data),

  clearClientFeature: (clientId: string, featureKey: string, facilityId?: string) =>
    api.delete(
      `${BASE}/clients/${clientId}/features/${featureKey}`,
      facilityId ? { params: { facility_id: facilityId } } : undefined,
    ),

  patchFeatureCatalog: (featureKey: string, patch: FeatureCatalogPatch) =>
    api
      .patch<{ data: FeatureCatalogRow }>(`${BASE}/features/${featureKey}`, patch)
      .then((r) => r.data.data),

  // ── Query Workflow v2 — master template library + cross-tenant health (slices 11, 12) ──

  listMasterQueryTemplates: () =>
    api
      .get<{ data: MasterQueryTemplate[] }>(`${BASE}/query-templates/master`)
      .then((r) => r.data.data),

  createMasterQueryTemplate: (body: NewMasterQueryTemplate) =>
    api
      .post<{ data: { id: string } }>(`${BASE}/query-templates/master`, body)
      .then((r) => r.data.data),

  updateMasterQueryTemplate: (
    templateId: string,
    patch: Partial<NewMasterQueryTemplate> & { is_active?: boolean },
  ) =>
    api
      .patch<{ data: { id: string; version: number } }>(`${BASE}/query-templates/master/${templateId}`, patch)
      .then((r) => r.data.data),

  getCrossTenantQueryHealth: () =>
    api
      .get<{ data: TenantQueryHealth[] }>(`${BASE}/query-health`)
      .then((r) => r.data.data),

  // ── Client Contracts ──

  listClientContracts: (clientId: string) =>
    api
      .get<{ data: ClientContract[] }>(`${BASE}/clients/${clientId}/contracts`)
      .then((r) => r.data.data),

  uploadClientContract: (clientId: string, formData: FormData) =>
    api
      .post<{ data: ClientContract }>(`${BASE}/clients/${clientId}/contracts`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data.data),

  getClientContractDownloadUrl: (clientId: string, contractId: string) =>
    api
      .get<{ data: { url: string; file_name: string } }>(
        `${BASE}/clients/${clientId}/contracts/${contractId}/download`,
      )
      .then((r) => r.data.data),

  streamClientContract: (clientId: string, contractId: string) =>
    api
      .get<ArrayBuffer>(
        `${BASE}/clients/${clientId}/contracts/${contractId}/stream`,
        { responseType: 'arraybuffer' },
      )
      .then((r) => r.data),

  updateClientContract: (clientId: string, contractId: string, body: UpdateContractRequest) =>
    api
      .patch<{ data: ClientContract }>(
        `${BASE}/clients/${clientId}/contracts/${contractId}`,
        body,
      )
      .then((r) => r.data.data),

  deleteClientContract: (clientId: string, contractId: string) =>
    api.delete(`${BASE}/clients/${clientId}/contracts/${contractId}`),
}

export interface MasterQueryTemplate {
  id: string
  code: string
  title: string
  category: string
  recipient_role: 'provider' | 'client_admin' | 'platform_admin' | 'none'
  is_blocking: boolean
  sla_hours: number | null
  compliant_query_required: boolean
  is_active: boolean
  version: number
  created_at: string
  updated_at: string
  clone_count: number
  created_by: string | null
  updated_by: string | null
  created_by_name: string | null
  updated_by_name: string | null
}

export interface NewMasterQueryTemplate {
  code: string
  title: string
  category: string
  recipient_role: 'provider' | 'client_admin' | 'platform_admin' | 'none'
  is_blocking: boolean
  sla_hours: number | null
  compliant_query_required: boolean
  body_template: string | null
}

export interface TenantQueryHealth {
  client_id: string
  client_name: string
  open_count: number
  expired_count: number
  resolved_30d: number
  provider_response_rate_30d: number
  force_resolve_count_30d: number
}

export type ContractType = 'msa' | 'baa' | 'sow' | 'nda' | 'addendum' | 'renewal' | 'other'
export type ContractStatus = 'draft' | 'active' | 'expired' | 'superseded'
export type ContractExpiryStatus = 'ok' | 'near' | 'critical' | 'expired' | null

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  msa: 'Master Services Agreement',
  baa: 'Business Associate Agreement',
  sow: 'Statement of Work',
  nda: 'Non-Disclosure Agreement',
  addendum: 'Addendum / Amendment',
  renewal: 'Renewal',
  other: 'Other',
}

export interface ClientContract {
  id: string
  client_id: string
  contract_type: ContractType
  title: string
  status: ContractStatus
  effective_date: string | null
  expiry_date: string | null
  blob_path: string
  file_name: string
  file_size_bytes: number | null
  content_type: string
  uploaded_by: string | null
  uploaded_by_name: string | null
  uploaded_at: string
  notes: string | null
  created_at: string
  updated_at: string
  days_until_expiry: number | null
  expiry_status: ContractExpiryStatus
}

export interface UpdateContractRequest {
  title?: string
  contract_type?: ContractType
  effective_date?: string | null
  expiry_date?: string | null
  status?: ContractStatus
  notes?: string | null
}
